import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { VisitResourcePackage } from "../resources/VisitResourcePackage";
import { Container, IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { gsap } from "gsap";
import { DefaultButton } from "../../../common/components/DefaultButton";
import { Hand } from "../../../common/components/Hand";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { CoinsCollectAnimationView } from "../../../common/components/animations/CoinsCollectAnimationView";
import { Card } from "../components/Card";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { VisitCard } from "../../../game/flow/impl/diceActions/visitAction/VisitCard";
import { GameFont } from "../../../common/GameFont";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { IVisitPopupController } from "../controller/IVisitPopupController";
import { NoCacheDynamicPackage } from "../../../common/resources/NoCacheDynamicPackage";
import { VisitAction } from "../../../game/flow/impl/diceActions/visitAction/VisitAction";
import { TickerDelay } from "../../../../../tools/TickerDelay";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";

export class VisitPopupView extends BaseScalableView<void> {
    private static readonly CARDS_CONTAINER_SCALE = 1.05;
    private static readonly RADIANS_DEFAULT_VALUE = 6.28319;
    private static readonly SWITCH_CARDS_ANIMATION_TIME = 0.3;

    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly visitResources: VisitResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly visitPopupController: IVisitPopupController;
    private readonly playerModel: IPlayerModel;
    private readonly visitDynamicResources: NoCacheDynamicPackage;
    private readonly action: VisitAction;
    private fadeScreen: FadeScreen;
    private cardsContainer: Container;
    private visitCostText: Text;
    private cards: Array<Card>;
    private cardsMoveStartPosition: Point;
    private topPanel: TopPanel;
    private coinsSubtractAnimation: CoinsCollectAnimationView;
    private coinsSubtractPoint: Point;
    private isFirstShow: boolean;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        visitResources: VisitResourcePackage,
        visitPopupController: IVisitPopupController,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler,
        isFirstShow: boolean,
        operationControls: PromiseCompletionSource<void>,
        topPanel: TopPanel,
        userProfileResources: UserProfileResourcePackage,
        action: VisitAction,
        visitDynamicResources: NoCacheDynamicPackage
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.visitResources = visitResources;
        this.visitDynamicResources = new NoCacheDynamicPackage("Visit");
        this.userProfileResources = userProfileResources;
        this.visitPopupController = visitPopupController;
        this.playerModel = playerModel;
        this.isFirstShow = isFirstShow;
        this.topPanel = topPanel;
        this.action = action;
        this.visitDynamicResources = visitDynamicResources;
        this.cards = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    public async start(): Promise<void> {
        await this.showFirstComponents();
        await this.selectCard(this.cards[0]);
        await TickerDelay.delay(300);
        await this.visitClickHandle();
    }

    public async build(): Promise<void> {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.cardsContainer = new Container();
        this.cardsContainer.scale.set(VisitPopupView.CARDS_CONTAINER_SCALE);
        this.addChild(this.cardsContainer);
        this.cardsContainer.addChild(this.cardContainerFactory());
        this.visitCostText = new Text(`Visit for ${StringFormatExtention.formatWithComma(this.action.diceRollVisitCard.costInCoins)} coins`, new VisitCostTextStyle());
        this.visitCostText.pivot.set(this.visitCostText.width * 0.5, this.visitCostText.height * 0.5);
        this.addChild(this.visitCostText);
        this.topPanel.bringToFront();
        this.coinsSubtractPoint = new Point();
        this.coinsSubtractAnimation = new CoinsCollectAnimationView(this.commonResources, 15, this.coinsSubtractPoint);
        this.coinsSubtractAnimation.scale.set(0.9);
        this.addChild(this.coinsSubtractAnimation);
    }

    private cardContainerFactory(): Card {
        const card = new Card(this.commonResources, this.visitResources, this.userProfileResources, this.canvasScaler);
        this.cards.push(card);
        return card;
    }

    private async showFirstComponents(): Promise<void> {
        this.interactiveCards(false);
        this.topPanel.visible = false;
        this.visitCostText.visible = false;
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.fadeScreen.fadeIn();
        this.cards.forEach(card => {
            animationTimeLine.fromTo(card, { x: this.cardsMoveStartPosition.x, y: this.cardsMoveStartPosition.y, angle: card.angle - 90 }, {
                x: card.x,
                y: card.y,
                angle: card.angle,
                duration: 0.5,
                ease: "back.out(1)",
                onStart: () => this.soundResources.assets_sounds_Popups_Entering_mp3.play()
            }, "<25%");
        });
        await animationTimeLine;
        animationTimeLine.kill();
        this.interactiveCards(true);
    }

    private async selectCard(selectedCard: Card): Promise<void> {
        this.interactiveCards(false);
        await this.playSelectCard(selectedCard);
        await this.startSwitchAndUpdateCards(selectedCard);
        if (this.isFirstShow) {
            const cardTutorialAnimation = this.currentAnimation = selectedCard.getTutorialAnimations();
            await cardTutorialAnimation.play();
        } else {
            selectedCard.fadeIn();
        }
        await this.showLatestComponents();
    }

    private async showLatestComponents(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.visitCostText, { visible: true });
        animationTimeLine.set(this.topPanel, { visible: true });
        animationTimeLine.add(() => this.topPanel.show());
        animationTimeLine.fromTo(this.visitCostText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.3, ease: "back.out(2)" }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async visitClickHandle(): Promise<void> {
        this.playerModel.subtractCoins(this.action.diceRollVisitCard.costInCoins);
        this.soundResources.assets_sounds_Coins_Top_Bar_mp3.play();
        await this.coinsSubtractAnimation.startAnimation();
        this.hideView();
    }

    private async playSelectCard(card: Card): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.to(card, { y: card.y - 100, x: card.x + 100 * card.rotation, duration: 0.1, yoyo: true, repeat: 1 });
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async startSwitchAndUpdateCards(selectedCard: Card): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.soundResources.assets_sounds_Card_Flip_mp3.play();

        //start fly and rotate cards
        animationTimeLine.to(this.cardsContainer.scale, { x: VisitPopupView.CARDS_CONTAINER_SCALE * 0.5, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power.in" }, "<");
        animationTimeLine.to(this.cardsContainer.skew, { y: VisitPopupView.RADIANS_DEFAULT_VALUE * 0.25, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "none" }, "<");
        animationTimeLine.to(selectedCard, { y: selectedCard.y - selectedCard.height * 1.1, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        animationTimeLine.to(selectedCard, { rotation: -VisitPopupView.RADIANS_DEFAULT_VALUE * 0.5, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.in" }, "<");

        //update cards
        animationTimeLine.add(() => {
            this.cardsContainer.setChildIndex(selectedCard, this.cards.length - 1);
            this.updateCards(selectedCard);
        });
        //finish fly and rotate cards
        animationTimeLine.set(this.cardsContainer.skew, { y: VisitPopupView.RADIANS_DEFAULT_VALUE * 0.75 }, "<");
        animationTimeLine.to(this.cardsContainer.scale, { x: VisitPopupView.CARDS_CONTAINER_SCALE, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" });
        animationTimeLine.to(this.cardsContainer.skew, { y: VisitPopupView.RADIANS_DEFAULT_VALUE, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        animationTimeLine.to(selectedCard, { y: selectedCard.y, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.in" }, "<");
        animationTimeLine.to(selectedCard, { rotation: -VisitPopupView.RADIANS_DEFAULT_VALUE, duration: VisitPopupView.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private updateCards(willBeChosenCard: Card): void {
        willBeChosenCard.updateAndShowCardContent(this.action.diceRollVisitCard, this.visitDynamicResources);
    }

    private async hideView(): Promise<void> {
        this.fadeScreen.fadeOut();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.operationControls.complete();
    }


    private interactiveCards(isLock: boolean): void {
        this.cards.forEach(card => card.eventMode = isLock ? "dynamic" : "none");
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.coinsSubtractAnimation.isActive) {
            this.coinsSubtractAnimation.setAnimationProgress(1);
        }
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.cardsContainer.position.set(width * 0.5, height * 0.5);
        this.layoutCards(width, height);
        this.cardsMoveStartPosition = new Point(0, height);
        this.coinsSubtractAnimation.position.set(width * 0.45, 0);
        this.coinsSubtractPoint.set(0, height * 0.6);
        this.visitCostText.position.set(width * 0.5, 1600 - this.visitCostText.height);
    }

    private layoutCards(width: number, height: number): void {
        const cardAngle = 15;
        let cardIndex = 0;
        this.cards.forEach(card => {
            card.angle = cardAngle * cardIndex;
            card.position.x = card.width * 0.25 * cardIndex;
            card.position.y = cardIndex === 0 ? 0 : card.height * 0.05;
            cardIndex--;
            card.layout(width, height);
        })
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}

class VisitCostTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#feca3b",
                "#f49801"
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 90,
            stroke: "#ffffff",
            letterSpacing: -3,
            strokeThickness: 10,
            lineJoin: "round"
        }));
    }
}
